import { getPlatformId, getRegistryUrl } from './compass-services';
import { requirejs } from './require';

type QueryParam = {
  url: string;
  method: string;
  options?: QueryOption;
};

type QueryOption = {
  data?: string | Record<string, unknown>;
  query?: Record<string, unknown>;
  tenant?: string;
  onUploadProgress?: () => void;
  noThrottle?: boolean;
  cache?: boolean;
  force?: boolean;
  unique?: boolean;
  service?: string;
  form?: unknown;
  file?: boolean;
  timeout?: number;
  noPassportRedirection?: boolean;
  contentType?: Record<string, string>;
  csv?: string;
  url?: string;
};

type TransformedQueryOption = QueryOption & {
  url: string;
  tenant: string;
};

const CACHE_THROTTLE: Record<string, unknown> = {};
const CACHE_REQUESTS: Record<string, unknown> = {};
const CACHE_UNIQUE: Record<string, any> = {};
const CACHE_SERVICES: Record<string, Record<string, string>> = {};

const get = async (arg0: string, options = {}) => query({ url: arg0, method: 'GET', ...options });
const post = async (arg0: string, options = {}) => query({ url: arg0, method: 'POST', ...options });
const patch = async (arg0: string, options = {}) =>
  query({ url: arg0, method: 'PATCH', ...options });
const put = async (arg0: string, options = {}) => query({ url: arg0, method: 'PUT', ...options });
const remove = async (arg0: string, options = {}) =>
  query({ url: arg0, method: 'DELETE', ...options });

const getTenant = async () => getPlatformId();

/*
 * Resolve a service URL, by default on the platform Make runs on.
 *
 * `tenant` targets another platform: a part opened from an addin lives in the
 * 3DDrive of the tenant it came from, and that service has its own host — asking
 * our own 3DDrive for its id answers 404.
 */
const getServiceURL = async (service: string, tenant?: string) => {
  const registryURL = getRegistryUrl();
  const platformId = tenant || getPlatformId();

  const [i3DXCompassRegistryClient] = await requirejs<[any]>([
    'DS/i3DXCompassPlatformServices/i3DXCompassRegistryClient',
  ]);

  if (!CACHE_SERVICES[platformId]) {
    CACHE_SERVICES[platformId] = {};
  }

  if (CACHE_SERVICES[platformId][service]) {
    return CACHE_SERVICES[platformId][service];
  }

  const [response] = await i3DXCompassRegistryClient.getServicesByPlatform({
    platforms: [platformId],
    services: [service],
    config: { url: registryURL },
  });

  CACHE_SERVICES[platformId][service] = response?.services[0].url || undefined;

  return (response?.services[0].url as string) || undefined;
};

/*
 * Merge query params into a URL that may already carry some.
 *
 * Callers can hand over a ready-made URL — a 3DDrive `fileurl` keeps the `?tenant=`
 * of the platform the file lives on — so appending a second `?` built
 * `?tenant=A?tenant=B`, read by the service as one bogus tenant value (404).
 * Params already in the URL win: they were built for that specific resource.
 */
export const _mergeQueryParams = (url: string, query: Record<string, unknown>) => {
  const [path, ...rest] = url.split('?');
  const urlQuery = rest.join('&');
  const urlKeys = new Set(
    urlQuery
      .split('&')
      .filter(Boolean)
      .map((param) => param.split('=')[0]),
  );
  const addedQuery = Object.entries(query)
    .filter(([key]) => !urlKeys.has(key))
    .map((el) => el.join('='))
    .join('&');

  return [path, [urlQuery, addedQuery].filter(Boolean).join('&')].filter(Boolean).join('?');
};

const setupQueryOption = async (tenant: string, params: QueryOption) => {
  const options: QueryOption = params || {};
  // SETUP DATA
  if (options.data && typeof options.data !== 'string') {
    options.data = JSON.stringify(options?.data);
  }

  // SETUP QUERY PARAMS
  if (!options?.query || typeof options?.query !== 'object') {
    options.query = {};
  }

  // SETUP TENANT
  // `options.tenant` targets another platform than the current one: an addin opens
  // Make with a part stored in the 3DDrive of the tenant it came from.
  options.query.tenant = options.tenant || tenant;
  options.query.xrequestedwith = 'xmlhttprequest';
  options.url = _mergeQueryParams(options.url as string, options.query);

  return options as TransformedQueryOption;
};

const _isCompleteURL = (url: string) => /^((http|https|ftp):\/\/)/.test(url);

const query = async (params: QueryParam) => {
  let baseUrl: string;
  const WAF = await getWAFData();
  const tenant = getPlatformId();
  const options = await setupQueryOption(tenant, params || {});

  if (params.method === 'GET') {
    if (CACHE_THROTTLE[options.url] && !options.noThrottle) {
      return CACHE_THROTTLE[options.url];
    }

    if (CACHE_REQUESTS[options.url] && options.cache && !options.force) {
      return CACHE_REQUESTS[options.url];
    }
  }

  if (options.unique && CACHE_UNIQUE[options.url]) {
    CACHE_UNIQUE[options.url].cancel();
    CACHE_UNIQUE[options.url].reject({ aborted: !0, url: options.url, method: params.method });

    delete CACHE_UNIQUE[options.url];
  }

  if (!_isCompleteURL(options.url)) {
    baseUrl = await getServiceURL(options.service || 'mpdatamgt', options.tenant);
  }

  const optionalHeaders: Record<string, unknown> = {};
  if (options.csv) {
    optionalHeaders.Accept = 'text/csv';
  }

  const prm = new Promise((resolve, reject) => {
    const request = WAF.authenticatedRequest((baseUrl || '') + options.url, {
      responseType: options.file ? 'blob' : 'json',
      type: 'json',
      headers: {
        'Content-Type': options.contentType
          ? options.contentType
          : options.data
            ? 'application/json'
            : undefined,
        ...optionalHeaders,
      },
      method: params.method || 'GET',
      data: options.data || options.form,
      timeout: options.timeout || 90000,
      onComplete: resolve,
      onFailure(error: string, _response: string) {
        const throwedError = new Error(error);
        /* TODO Tracker */
        reject(throwedError);
      },
      onUploadProgress: options.onUploadProgress,
      async onPassportError() {
        if (options.noPassportRedirection) {
          reject();
          return;
        }

        window.location.assign(
          `${await getServiceURL('3DPassport')}/login?service=${encodeURIComponent(
            window.location.href,
          )}`,
        );
      },
    });

    if (options.unique) {
      CACHE_UNIQUE[options.url] = { cancel: request.cancel, reject };
    }
  }).finally(() => {
    delete CACHE_UNIQUE[options.url];
    setTimeout(() => {
      delete CACHE_THROTTLE[options.url];
    }, 2000);
  });

  if (params.method === 'GET') {
    CACHE_THROTTLE[options.url] = prm;
    if (options.cache) {
      CACHE_REQUESTS[options.url] = prm;
    }
  }

  return prm;
};

const redirectToPassport = async (to = '', params = '') => {
  const passportUrl = await getServiceURL('3DPassport');
  const urlTo = `${to || window.location.href}?${params}`;
  window.location.assign(`${passportUrl}/login?service=${encodeURIComponent(urlTo)}`);
};

const getWAFData = async () => {
  const [WAFData] = await requirejs<[any]>(['DS/WAFData/WAFData']);
  return WAFData;
};

export default {
  get,
  post,
  remove,
  put,
  patch,
  getServiceURL,
  getTenant,
  redirectToPassport,
  getWAF: getWAFData,
};
